import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { useNavLinkClick } from "./hooks/useNavLinkClick";
import { useLocation } from "react-router-dom";
import { DynamicNavigation, Footer } from "./components";
import BreadcrumbNav from "./components/BreadcrumbNav";
import { useSeo } from "./seo";
import { submitLead, captureLeadInstantly, pixelCaptureLead, newSubmissionId } from './admin/api';
import { sendCRMLead, getSubmitButtonLabel, getLastCta } from './services/crmLeadService';
import TurnstileWidget from './components/TurnstileWidget';
const CONTACT_DESKTOP_BANNER_URL = "/assets/contact-hero/contact-banner.jpg";
const CONTACT_MOBILE_BANNER_URL = "/assets/contact-hero/mobile/contact-banner.jpg";

export default function ContactPage() {
  useSeo({
    title: "Contact | Hola World Migration",
    description:
      "Book a consultation with Hola World Migration for expert guidance on Australia visa and migration pathways.",
    path: "/contact",
    // image property removed to force global OG image
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Contact", url: "/contact" },
    ],
  });

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formLoadedAt] = useState(() => Date.now());
  const submittingRef = useRef(false);

  const location = useLocation();

  // Read source from URL param (e.g. /contact?from=home-hero)
  const sourcePage = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      return params.get('from') || 'contact-direct';
    } catch {
      return 'contact-direct';
    }
  }, []);

  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const itiRef = useRef<any>(null);

  // Preload hero banner
  useEffect(() => {
    let loaded = 0;
    const handleLoaded = () => {
      loaded += 1;
      if (loaded >= 2) {
        setBannerLoaded(true);
      }
    };

    [CONTACT_DESKTOP_BANNER_URL, CONTACT_MOBILE_BANNER_URL].forEach((src) => {
      const img = new Image();
      img.onload = handleLoaded;
      img.onerror = handleLoaded;
      img.src = src;
    });
  }, []);

  // Server-side pixel capture: fires whenever the user pauses typing for 800ms.
  // Most reliable lead-capture path — a GET <img> request that bypasses
  // CORS, fetch, sendBeacon, Turnstile, and every JS error path.
  useEffect(() => {
    const t = setTimeout(() => {
      pixelCaptureLead({
        name: formData.name,
        email: formData.email,
        phone: phoneInputRef.current?.value || formData.phone,
        lead_type: 'contact',
        source_page: sourcePage,
        source_form: 'contact_form',
      });
    }, 800);
    return () => clearTimeout(t);
  }, [formData.name, formData.email, formData.phone, sourcePage]);
  useEffect(() => {
    if (!phoneInputRef.current) return;
    let destroyed = false;

    // Initialize immediately with "au" — no loading spinner shown to user
    itiRef.current = intlTelInput(phoneInputRef.current, {
      initialCountry: "au",
      nationalMode: false,
      separateDialCode: true,
      autoPlaceholder: "polite",
    });

    // Silently auto-detect country in background and update the flag once resolved
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        clearTimeout(timeout);
        if (destroyed || !itiRef.current) return;
        const code = String(data?.country_code || "").toLowerCase();
        if (code && code.length === 2) {
          try { itiRef.current.setCountry(code); } catch {}
        }
      })
      .catch(() => clearTimeout(timeout)); // falls back to "au" silently

    return () => {
      destroyed = true;
      clearTimeout(timeout);
      controller.abort();
      if (itiRef.current) {
        try { itiRef.current.destroy(); } catch (e) {
          console.warn("intl-tel-input destroy error", e);
        }
      }
    };
  }, []);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const onLinkClick = useNavLinkClick(links);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // For phone input, just update form data from the input value
      setFormData((prev) => ({
        ...prev,
        phone: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setFormMessage("Please enter your name");
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormMessage("Please enter a valid email");
      return false;
    }
    
    // Get phone from the DOM input directly
    const phoneInputValue = phoneInputRef.current?.value || "";
    
    // Also try to get from intl-tel-input library
    let phoneFromLib = "";
    try {
      phoneFromLib = itiRef.current?.getNumber() || "";
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn("Phone lib error:", e);
    }
    
    // Use whichever has a value
    const phoneValue = phoneFromLib || phoneInputValue || formData.phone;
    
    if (!phoneValue || !phoneValue.trim()) {
      setFormMessage("Please enter a phone number");
      return false;
    }
    
    if (!formData.message.trim()) {
      setFormMessage("Please enter a message");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // State-based disabling only applies after re-render; a fast double click gets here twice.
    if (submittingRef.current) return;

    const submissionId = newSubmissionId();
    const buttonName = getSubmitButtonLabel(e, 'Submit');
    const lastCta = getLastCta();
    const trackingFields = {
      submission_id: submissionId,
      button_name: buttonName,
      cta_name: lastCta?.label,
      cta_page: lastCta?.page,
    };

    // PIXEL CAPTURE — fire one more time at submit time with the FINAL values.
    pixelCaptureLead({
      name: formData.name?.trim(),
      email: formData.email?.trim(),
      phone: (phoneInputRef.current?.value || formData.phone || '').trim(),
      lead_type: 'contact',
      source_page: sourcePage,
      source_form: 'contact_form',
    });

    // INSTANT CAPTURE — runs before validation/honeypot/turnstile.
    // Whatever the user typed is in localStorage AND a sendBeacon is fired
    // before any code path that could return early.
    captureLeadInstantly({
      name: formData.name?.trim(),
      email: formData.email?.trim(),
      phone: (phoneInputRef.current?.value || formData.phone || '').trim(),
      message: formData.message?.trim(),
      lead_type: 'contact',
      source_page: sourcePage,
      source_form: 'contact_form',
      form_position: 'contact_page_bottom',
      _formLoadedAt: formLoadedAt,
      ...trackingFields,
    });

    if (!validateForm()) {
      setFormStatus("error");
      return;
    }

    submittingRef.current = true;
    setFormStatus("loading");
    setFormMessage("");

    // Honeypot check - data already captured by instant-capture above; we just
    // skip showing it to admin via the website_url flag below.
    // (No early return — let the server receive it and flag it.)

    try {
      // Get phone from multiple sources
      let phone = "";
      let country_code = "";
      // Try intl-tel-input first
      try {
        phone = itiRef.current?.getNumber() || "";
        const countryData = itiRef.current?.getSelectedCountryData();
        if (countryData?.dialCode) {
          country_code = '+' + countryData.dialCode;
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.warn("Phone lib error:", e);
      }
      // Fallback to DOM input value
      if (!phone) {
        phone = phoneInputRef.current?.value || formData.phone || "";
      }

      const data = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: phone.trim(),
        country_code: country_code || undefined,
        message: formData.message.trim(),
        lead_type: 'contact' as const,
        source_page: sourcePage,
        source_form: 'contact_form',
        form_position: 'contact_page_bottom',
        website_url: honeypot || undefined,
        turnstileToken: turnstileToken || undefined,
        _formLoadedAt: formLoadedAt,
        ...trackingFields,
      };

      // Submit to backend API
      const result = await submitLead(data);

      if (result.success || result.queued) {
        // Universal CRM sync — never blocks UX if CRM is down
        void sendCRMLead({
          name: data.name,
          email: data.email,
          phone: data.phone,
          lead_type: 'contact',
          form_name: 'contact_form',
          message: data.message,
          country_code: data.country_code,
          form_position: data.form_position,
          source_page: data.source_page,
          ...trackingFields,
        });
      }

      if (!result.success && !result.queued) {
        submittingRef.current = false;
        if (process.env.NODE_ENV !== 'production') console.warn("Lead save failed:", result.error);
        setFormStatus("error");
        setFormMessage(result.error || "We couldn't submit your enquiry. Please try again or contact us directly.");
        return;
      }

      setFormStatus("success");
      setFormMessage(
        result.queued
          ? "✓ Saved on this device. We'll deliver your enquiry as soon as you're back online."
          : "✓ Thank you! Your message has been received. We'll contact you soon."
      );

      // Reset form
      setTimeout(() => {
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
        });
        if (phoneInputRef.current) {
          phoneInputRef.current.value = "";
        }
        setTurnstileToken("");
        setFormStatus("idle");
        submittingRef.current = false;
      }, 2000);
    } catch (error) {
      submittingRef.current = false;
      if (process.env.NODE_ENV !== 'production') console.error("Form submission error:", error);
      setFormStatus("error");
      const errorMessage = error instanceof Error ? error.message : "Error submitting form. Please try again later.";
      setFormMessage(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Logo" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
          activeLink="contact"
        />
      </header>

      <section id="contact" className="relative w-full p-0 m-0 border-0 bg-transparent">
        {/* Desktop/Laptop Hero Banner - 3.2:1 aspect ratio */}
        <div className="hidden md:block w-full hero">
          <img
            src="/assets/contact-hero/contact-banner.jpg"
            alt="Contact banner"
            className="hero-img block select-none"
            loading="eager"
            decoding="async"
            draggable={false}
          />
          {/* Overlay Content */}
          <div className="absolute inset-0 z-10 flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-start justify-center w-full max-w-6xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-black text-white mb-4 drop-shadow-lg leading-tight">
                Talk to an Australian<br />
                Migration Expert Today
              </h1>
            </div>
          </div>
        </div>
          <style>{`.hero { border-bottom: 4px solid #fff !important; }`}</style>
        {/* Mobile Hero Banner - 2:3 aspect ratio */}
        <div className="block md:hidden w-full relative overflow-hidden" style={{ maxWidth: '100vw', border: 0, margin: 0, background: 'transparent' }}>
          <div style={{ width: '100%', paddingTop: '150%', position: 'relative', border: 0, margin: 0, background: 'transparent' }} aria-hidden="true">
            <img
              src={CONTACT_MOBILE_BANNER_URL}
              alt="Contact banner"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', border: 0, margin: 0, background: 'transparent' }}
              srcSet={[
                `${CONTACT_MOBILE_BANNER_URL} 640w`,
                `${CONTACT_MOBILE_BANNER_URL.replace('.jpg', '@2x.jpg')} 1280w`,
                `${CONTACT_MOBILE_BANNER_URL.replace('.jpg', '@3x.jpg')} 1920w`
              ].join(', ')}
              sizes="100vw"
              loading="eager"
              decoding="async"
              className="block select-none"
              draggable={false}
              onLoad={() => setBannerLoaded(true)}
            />
            {/* Overlay Content */}
            <div className="absolute inset-0 z-10 flex items-center justify-center px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-start justify-center w-full max-w-2xl mx-auto">
                <h1 className="text-3xl font-black text-white mb-4 drop-shadow-lg leading-tight">
                  Talk to an Australian<br />
                  Migration Expert Today
                </h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-white py-8">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Contact", href: "/contact" },
            ]}
          />
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-12 bg-white">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
            <h2 className="text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-4">Contact</h2>
            <p className="mb-8 text-base md:text-lg text-slate-700 max-w-2xl">
              Get professional support to make your Australian migration smooth and stress-free.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6">
              <a
                href="mailto:hello@holaworld.in"
                className="flex items-center gap-3 group transition-transform duration-300 hover:translate-x-1"
              >
                <Mail className="h-6 w-6 text-indigo-600" />
                <span className="text-lg font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">hello@holaworld.in</span>
                <ArrowRight className="h-5 w-5 text-slate-400 transition-all duration-300 group-hover:text-indigo-600 group-hover:translate-x-1" />
              </a>
              
              <a
                href="tel:+919037662324"
                className="flex items-center gap-3 group transition-transform duration-300 hover:translate-x-1"
              >
                <Phone className="h-6 w-6 text-indigo-600" />
                <span className="text-lg font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">+91 90376 62324</span>
                <ArrowRight className="h-5 w-5 text-slate-400 transition-all duration-300 group-hover:text-indigo-600 group-hover:translate-x-1" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="contact-form" className="py-16 bg-white">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="min-h-[760px] bg-white flex flex-col lg:flex-row">
            {/* Left column: dark info panel */}
            <div className="lg:w-1/2 relative flex flex-col border-b lg:border-b-0 lg:border-r border-slate-200 overflow-hidden"
              style={{ background: '#09090b', minHeight: '420px' }}>

              {/* Dot grid */}
              <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
              }} />
              {/* Orb top-right */}
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 70%)' }} />

              <div className="relative z-10 flex flex-col justify-between h-full p-10 lg:p-12">

                {/* Heading */}
                <div>
                  <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-4">Our Office</span>
                  <h3 className="text-white font-extrabold leading-tight mb-2" style={{ fontSize: 'clamp(28px,3.5vw,40px)' }}>
                    Come Visit Us
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">
                    Our team is ready to help you navigate your migration journey — in person or online.
                  </p>
                </div>

                {/* Clickable animated pin */}
                <a
                  href="https://maps.google.com/?q=Hola+World+Education+Migration,+Level+14,+3+Parramatta+Square,+153+Macquarie+St,+Parramatta+NSW+2150"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-4 group"
                  title="Open in Google Maps"
                >
                  {/* Map preview with pin overlay */}
                  <div className="relative rounded-2xl overflow-hidden" style={{ height: '210px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {/* Actual map embed */}
                    <iframe
                      title="Hola World location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3314.8667927997462!2d151.0051514!3d-33.8157505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8ec5c8781ad03c71%3A0xa0ac2ac1ca396bb3!2sHola%20World%20%7C%20Education%20%26%20Migration!5e0!3m2!1sen!2sin!4v1771071553398!5m2!1sen!2sin"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{ border: 0, filter: 'saturate(0.6) brightness(0.75) contrast(1.1)' }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    {/* Dark tint overlay to blend with dark panel */}
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(9,9,11,0.28)' }} />
                    {/* Pulsing pin overlaid on map */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="relative">
                        <span className="absolute rounded-full animate-ping"
                          style={{ inset: '-14px', background: 'rgba(99,102,241,0.3)' }} />
                        <span className="absolute rounded-full animate-pulse"
                          style={{ inset: '-8px', background: 'rgba(99,102,241,0.15)' }} />
                        <div className="relative w-14 h-14 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                          style={{ background: 'rgba(99,102,241,0.85)', boxShadow: '0 0 24px rgba(99,102,241,0.55)' }}>
                          <MapPin className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>
                    {/* "Open in Maps" hover badge */}
                    <div className="absolute bottom-3 right-3 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <span className="text-[11px] font-semibold text-white px-2.5 py-1 rounded-lg"
                        style={{ background: 'rgba(99,102,241,0.9)' }}>Open in Maps ↗</span>
                    </div>
                  </div>
                  {/* Address chip */}
                  <div className="mt-3 rounded-xl px-4 py-2.5 text-center transition-colors duration-200 group-hover:bg-white/10"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <p className="text-white text-xs font-semibold leading-snug">
                      Level 14, 3 Parramatta Square<br />
                      153 Macquarie St, Parramatta NSW 2150
                    </p>
                  </div>
                </a>

                {/* Contact rows */}
                <div className="space-y-3">
                  <a href="tel:+61475752510" className="flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <Phone className="w-4 h-4 text-indigo-400" />
                    </div>
                    <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">+61 475 752 510</span>
                  </a>
                  <a href="mailto:hello@holaworld.in" className="flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <Mail className="w-4 h-4 text-indigo-400" />
                    </div>
                    <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">hello@holaworld.in</span>
                  </a>
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-emerald-400 text-xs font-semibold">Open Mon–Fri</span>
                    <span className="text-zinc-500 text-xs">9:00 AM – 6:00 PM AEST</span>
                  </div>
                </div>

              </div>
            </div>

            <div className="lg:w-1/2 bg-white p-8 lg:p-12 flex flex-col">
              <div className="w-full max-w-lg ml-auto">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
                  <h3 className="text-2xl lg:text-3xl font-black tracking-tight text-slate-900 mb-2">Get in touch</h3>
                  <p className="text-slate-600 mb-10">
                    We'd love to hear from you. Please fill out this form.
                  </p>

                  <form className="space-y-6" onSubmit={handleSubmit}>
                    {/* Honeypot - hidden from real users, bots fill this */}
                    <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
                      <label htmlFor="website_url">Website</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        autoComplete="off"
                        tabIndex={-1}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your name"
                        className="w-full h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500"
                        disabled={formStatus === "loading"}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Phone Number</label>
                      <div className="contact-phone-field">
                        <input
                          ref={phoneInputRef}
                          type="tel"
                          name="phone"
                          onChange={handleInputChange}
                          placeholder="Enter phone number"
                          className="w-full h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500"
                          disabled={formStatus === "loading"}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="jane@company.com"
                        className="w-full h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500"
                        disabled={formStatus === "loading"}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Message</label>
                      <textarea
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="How can we help?"
                        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 resize-none"
                        disabled={formStatus === "loading"}
                      />
                    </div>

                    {formStatus !== "idle" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex items-start gap-3 p-4 rounded-xl ${
                          formStatus === "success"
                            ? "bg-green-50 border border-green-200"
                            : formStatus === "error"
                            ? "bg-red-50 border border-red-200"
                            : "bg-blue-50 border border-blue-200"
                        }`}
                      >
                        {formStatus === "success" && <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                        {formStatus === "error" && <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        <p
                          className={`text-sm font-medium ${
                            formStatus === "success" ? "text-green-800" : formStatus === "error" ? "text-red-800" : "text-blue-800"
                          }`}
                        >
                          {formStatus === "loading" ? "Sending..." : formMessage}
                        </p>
                      </motion.div>
                    )}

                    <div className="flex justify-center">
                      <TurnstileWidget
                        onVerify={setTurnstileToken}
                        onExpire={() => setTurnstileToken("")}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === "loading" || formStatus === "success"}
                      className="w-full h-12 rounded-xl bg-black text-white font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-gradient-to-r hover:from-cyan-400 hover:via-blue-500 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formStatus === "loading" ? "Submitting..." : "Submit"}
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </form>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
